// store/slices/closeTicketSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type ActiveTab = 'CURRENT' | 'SCHEDULED_RATE_CHANGE' | 'SCHEDULED_TAX_FEE_CHANGE';

interface State {
  recurringTickets: any[];
  activeTab: ActiveTab;
  addCustomRate: boolean;
  formattedTaxFees: any[];
  currentRateDetails: any[];
  currentRateDetailsTaxFees: any[];
  currentRateDetailsTaxFeesJurisdictionDetails: any[];
  scheduledRateDetails: any[];
  scheduledRateDetailsTaxFees: any[];
  scheduledRateDetailsTaxFeesJurisdictionDetails: any[];
  scheduledTaxFeeDetails: any[];
  scheduledTaxFeeDetailsTaxFees: any[];
  scheduledTaxFeesJurisdictionDetails: any[];
  openRateDetailWarningModal: boolean;
  rateDetailWarningMessage: string;
  ticketNumber: string;
  weightMeasurementUnits: { label: string; value: string }[];
  inputWeight: string;
  documentFileId: string | null;
  fieldName: string | null;
  fieldValue: string | null;
  openCombinationChangeWarningModal: boolean;
  isCombinationChangeConfirmed: boolean;
  ticketAction: string | null;
  openQuickCloseWarningModal: boolean;
  openRateDetailActionModal: boolean;
  editableRateDetailId: string | null;
  openTicketIsClosedWarningModal: boolean;
  openTargetDateBeforeFirstRateEffectiveDateWarningModal: boolean;
}

const initialState: State = {
  recurringTickets: [],
  activeTab: 'CURRENT',
  addCustomRate: false,
  formattedTaxFees: [],
  currentRateDetails: [],
  currentRateDetailsTaxFees: [],
  currentRateDetailsTaxFeesJurisdictionDetails: [],
  scheduledRateDetails: [],
  scheduledRateDetailsTaxFees: [],
  scheduledRateDetailsTaxFeesJurisdictionDetails: [],
  scheduledTaxFeeDetails: [],
  scheduledTaxFeeDetailsTaxFees: [],
  scheduledTaxFeesJurisdictionDetails: [],
  openRateDetailWarningModal: false,
  rateDetailWarningMessage: '',
  ticketNumber: '',
  weightMeasurementUnits: [],
  inputWeight: '0.00',
  documentFileId: null,
  fieldName: null,
  fieldValue: null,
  openCombinationChangeWarningModal: false,
  isCombinationChangeConfirmed: false,
  ticketAction: null,
  openQuickCloseWarningModal: false,
  openRateDetailActionModal: false,
  editableRateDetailId: null,
  openTicketIsClosedWarningModal: false,
  openTargetDateBeforeFirstRateEffectiveDateWarningModal: false
};

const closeTicket = createSlice({
  name: 'closeTicket',
  initialState,
  reducers: {
    setRecurringTickets(state, action: PayloadAction<any[]>) {
      state.recurringTickets = action.payload;
    },
    updateCurrentTab(state, action: PayloadAction<ActiveTab>) {
      state.activeTab = action.payload;
    },
    setAddCustomRate(state, action: PayloadAction<boolean>) {
      state.addCustomRate = action.payload;
    },
    setFormattedTaxFees(state, action: PayloadAction<any[]>) {
      state.formattedTaxFees = action.payload;
    },
    setCurrentRateDetails(state, action: PayloadAction<any[]>) {
      state.currentRateDetails = action.payload;
    },
    setCurrentRateDetailsTaxFees(state, action: PayloadAction<any[]>) {
      state.currentRateDetailsTaxFees = action.payload;
    },
    setCurrentRateDetailsTaxFeesJurisdictionDetails(state, action: PayloadAction<any[]>) {
      state.currentRateDetailsTaxFeesJurisdictionDetails = action.payload;
    },
    setScheduledRateDetails(state, action: PayloadAction<any[]>) {
      state.scheduledRateDetails = action.payload;
    },
    setScheduledRateDetailsTaxFees(state, action: PayloadAction<any[]>) {
      state.scheduledRateDetailsTaxFees = action.payload;
    },
    setScheduledRateDetailsTaxFeesJurisdictionDetails(state, action: PayloadAction<any[]>) {
      state.scheduledRateDetailsTaxFeesJurisdictionDetails = action.payload;
    },
    setScheduledTaxFeeDetails(state, action: PayloadAction<any[]>) {
      state.scheduledTaxFeeDetails = action.payload;
    },
    setScheduledTaxFeeDetailsTaxFees(state, action: PayloadAction<any[]>) {
      state.scheduledTaxFeeDetailsTaxFees = action.payload;
    },
    setScheduledTaxFeesJurisdictionDetails(state, action: PayloadAction<any[]>) {
      state.scheduledTaxFeesJurisdictionDetails = action.payload;
    },
    setOpenRateDetailWarningModal(state, action: PayloadAction<boolean>) {
      state.openRateDetailWarningModal = action.payload;
    },
    setRateDetailWarningMessage(state, action: PayloadAction<string>) {
      state.rateDetailWarningMessage = action.payload;
    },
    selectTicketNumber(state, action: PayloadAction<string>) {
      state.ticketNumber = action.payload;
    },
    setWeightMeasurementUnits(state, action: PayloadAction<{ label: string; value: string }[]>) {
      state.weightMeasurementUnits = action.payload;
    },
    setInputWeight(state, action: PayloadAction<string>) {
      state.inputWeight = action.payload;
    },
    setDocumentFileId(state, action: PayloadAction<string | null>) {
      state.documentFileId = action.payload;
    },
    setFieldName(state, action: PayloadAction<string | null>) {
      state.fieldName = action.payload;
    },
    setFieldValue(state, action: PayloadAction<string | null>) {
      state.fieldValue = action.payload;
    },
    setOpenCombinationChangeWarningModal(state, action: PayloadAction<boolean>) {
      state.openCombinationChangeWarningModal = action.payload;
    },
    setCombinationChangeConfirmed(state, action: PayloadAction<boolean>) {
      state.isCombinationChangeConfirmed = action.payload;
    },
    setTicketAction(state, action: PayloadAction<string | null>) {
      state.ticketAction = action.payload;
    },
    setOpenQuickCloseWarningModal(state, action: PayloadAction<boolean>) {
      state.openQuickCloseWarningModal = action.payload;
    },
    setOpenRateDetailActionModal(state, action: PayloadAction<boolean>) {
      state.openRateDetailActionModal = action.payload;
    },
    setEditableRateDetailId(state, action: PayloadAction<string | null>) {
      state.editableRateDetailId = action.payload;
    },
    setOpenTicketIsClosedWarningModal(state, action: PayloadAction<boolean>) {
      state.openTicketIsClosedWarningModal = action.payload;
    },
    setOpenTargetDateBeforeFirstRateEffectiveDateWarningModal(state, action: PayloadAction<boolean>) {
      state.openTargetDateBeforeFirstRateEffectiveDateWarningModal = action.payload;
    },
    resetCloseTicketState() {
      return initialState;
    }
  },
});

export const {
  setRecurringTickets,
  updateCurrentTab,
  setAddCustomRate,
  setFormattedTaxFees,
  setCurrentRateDetails,
  setCurrentRateDetailsTaxFees,
  setCurrentRateDetailsTaxFeesJurisdictionDetails,
  setScheduledRateDetails,
  setScheduledRateDetailsTaxFees,
  setScheduledRateDetailsTaxFeesJurisdictionDetails,
  setScheduledTaxFeeDetails,
  setScheduledTaxFeeDetailsTaxFees,
  setScheduledTaxFeesJurisdictionDetails,
  setOpenRateDetailWarningModal,
  setRateDetailWarningMessage,
  selectTicketNumber,
  setWeightMeasurementUnits,
  setInputWeight,
  setDocumentFileId,
  setFieldName,
  setFieldValue,
  setOpenCombinationChangeWarningModal,
  setCombinationChangeConfirmed,
  setTicketAction,
  setOpenQuickCloseWarningModal,
  setOpenRateDetailActionModal,
  setEditableRateDetailId,
  setOpenTicketIsClosedWarningModal,
  setOpenTargetDateBeforeFirstRateEffectiveDateWarningModal,
  resetCloseTicketState
} = closeTicket.actions;

export default closeTicket.reducer;
